/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib — opt-in «nothing changed since the form opened» answer for a
 *              record, read by BbModal before it asks «Sono presenti modifiche non
 *              salvate» (M11 W3, finding J2/B4)
 *   exports:   registerPristineCheck(record, fn) → unregister(),
 *              isRegisteredPristine(record)
 *   uses:      nothing (a module WeakMap keyed by the record)
 *   siblings:  components/bb-modal.js (the only reader); writer: lib/pristine-guard.js
 *              (PristineGuard, used by invoice / payment / expense / event «UI nuova» edit
 *              forms); project-edit-nuova.js and clinic-form-step-nuova.js (beebeeboard-medical)
 *              register their own checks
 *   gotchas:   a NEW Ember Data record is ALWAYS `hasDirtyAttributes` (isNew), so
 *              every untouched «Nuova fattura» asked before closing. A form that
 *              knows better registers a check; without one the modal keeps the
 *              classic hasDirtyAttributes answer (classic UI unchanged). The check
 *              must answer false whenever it is unsure (not armed yet, throws):
 *              a false «pristine» would close a form with real changes. BbModal
 *              honours it for NEW records only: an existing record always gets the
 *              alert, whose «close without saving» rolls the record back.
 */
const checks = new WeakMap();

/**
 * @param {object} record the record a modal passes as @item
 * @param {() => boolean} fn true ONLY when nothing the user can lose changed
 * @returns {() => void} unregister (removes only this fn)
 */
function registerPristineCheck(record, fn) {
  if (!record || typeof record !== 'object' && typeof record !== 'function' || typeof fn !== 'function') {
    return () => {};
  }
  checks.set(record, fn);
  return () => {
    if (checks.get(record) === fn) {
      checks.delete(record);
    }
  };
}

/**
 * @param {object} record
 * @returns {boolean} true only when a registered check says «pristine»
 */
function isRegisteredPristine(record) {
  if (!record || typeof record !== 'object' && typeof record !== 'function') return false;
  const fn = checks.get(record);
  if (typeof fn !== 'function') return false;
  try {
    return fn() === true;
  } catch {
    return false;
  }
}

export { isRegisteredPristine, registerPristineCheck };
//# sourceMappingURL=pristine-registry.js.map
